import React, { useEffect, useState, useRef } from 'react';
import { Avatar, Layout, Menu, Badge, Button } from 'antd';
import {
  BellOutlined,
  SunOutlined,
  MoonOutlined,
  MenuOutlined,
  HomeOutlined
} from '@ant-design/icons';
import '../styles/pages/Admin.css';
import { Link, Outlet } from 'react-router-dom';
import MenuProfile from '../components/layouts/MenuProfile';
import { socket } from '../utils/services/socket';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store/store';
import type { MenuProps } from 'antd';

const { Header, Content, Footer, Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
  label: React.ReactNode,
  key: string,
  icon?: React.ReactNode,
  children?: MenuItem[]
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
  } as MenuItem;
}

const Sidebar: React.FC = () => {

  const { rex_user } = useSelector((state: RootState) => state.login);

  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [hovering, setHovering] = useState<boolean>(false);
  const [theme, setTheme] = useState<string>('light');
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const siderRef = useRef<HTMLDivElement>(null);
  const COLLAPSE_BREAKPOINT = 768;

  const items: MenuItem[] = [
    getItem(
      <div>Pacientes</div>,
      '9',
      <HomeOutlined
        className="stroke-[#808080] fill-[#C3C5C9] -ml-[15px]"
      />,
      [
        getItem(
          <><Link to='/pacientes/crear'>Crear Paciente</Link></>,
          '9.1',
          undefined,
          undefined
        ),
        getItem(
          <><Link to='/pacientes/lista'>Lista de Pacientes</Link></>,
          '9.2',
          undefined,
          undefined
        ),
      ]
    ),

    getItem(
      <div>Consultas</div>,
      '3',
      <HomeOutlined
        className="stroke-[#808080] fill-[#C3C5C9] -ml-[15px]"
      />,
      [
        getItem(
          <><Link to='/consultas/optometrias-neonatas'>Optometrias Neonatos</Link></>,
          '3.1',
          undefined,
          undefined
        ),
        getItem(
          <><Link to='/consultas/optometrias-pediatrica'>Optometría Pediátrica</Link></>,
          '3.2',
          undefined,
          undefined
        ),
        getItem(
          <><Link to='/consultas/ortoptica-vision-binocular'>Ortóptica - Visión Binocular</Link></>,
          '3.3',
          undefined,
          undefined
        ),
        getItem(
          <><Link to='/consultas/baja-vision'>Baja Visión</Link></>,
          '3.4',
          undefined,
          undefined
        ),
        getItem(
          <><Link to='/consultas/optometrias-general'>Optometría General</Link></>,
          '3.5',
          undefined,
          undefined
        ),
        getItem(
          <><Link to='/consultas/historia-clinica'>Historia Clínica</Link></>,
          '3.6',
          undefined,
          undefined
        ),
      ]
    ),

    getItem(
      <><Link to='/chat'>Chat</Link></>,
      '8',
      <HomeOutlined
        className="stroke-[#808080] fill-[#C3C5C9] "
      />,
      undefined
    ),

    getItem(
      <><Link to='/agenda'>Agenda</Link></>,
      '11',
      <HomeOutlined
        className="stroke-[#808080] fill-[#C3C5C9] "
      />,
      undefined
    ),

    getItem(
      <><Link to='/kpis'>Analytics</Link></>,
      '7',
      <HomeOutlined
        className="stroke-[#808080] fill-[#C3C5C9] "
      />,
      undefined
    ),

    getItem(
      <>Administrador</>,
      '13',
      <HomeOutlined
        className="stroke-[#808080] fill-[#C3C5C9] -ml-[15px]"
      />,
      [
        getItem(
          <><Link to='/administrador/usuarios'>Usuarios</Link></>,
          '14',
          undefined,
          undefined
        ),
        getItem(
          <><Link to='/administrador/tipos_usuarios'>Tipos Usuarios</Link></>,
          '15',
          undefined,
          undefined
        ),
        getItem(
          <><Link to='/administrador/sucursales'>Sucursales</Link></>,
          '16',
          undefined,
          undefined
        ),
        getItem(
          <><Link to='/administrador/empresas'>Empresas</Link></>,
          '17',
          undefined,
          undefined
        ),
      ]
    ),
  ];

  useEffect(() => {
    if (rex_user) {
      socket.emit('subscribeToSession', rex_user.id)
    }
  }, [rex_user])

  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      setWindowWidth(currentWidth);
      if (currentWidth <= COLLAPSE_BREAKPOINT) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleMouseEnter = (): void => {
    if (collapsed) {
      setHovering(true);
    }
  };

  const handleMouseLeave = (): void => {
    setHovering(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        siderRef.current &&
        event.target instanceof Node &&
        !siderRef.current.contains(event.target) &&
        hovering
      ) {
        setHovering(false);
      }
    };

    if (hovering) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [hovering]);

  useEffect(() => {
    if (!collapsed) {
      setHovering(false);
    }
  }, [collapsed]);

  return (
    <Layout
      style={{
        minHeight: '100vh',
        background: '#FFFFFF',
      }}
    >
      <div
        ref={siderRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="sidebar-container"
        style={{
          position: 'relative',
          zIndex: 1000,
        }}
      >
        <Sider
          className={`sidebar-home shadow-[5px_0_25px_0_rgba(94,92,154,0.1)] ${hovering ? 'floating-sidebar' : ''}`}
          collapsible
          collapsed={collapsed && !hovering}
          onCollapse={(value) => setCollapsed(value)}
          theme='light'
          trigger={null}
          style={{
            position: hovering ? 'absolute' : 'relative',
            height: '100%',
            transition: 'all 0.3s ease',
            width: hovering ? 250 : (collapsed ? 80 : 250),
            zIndex: hovering ? 1001 : 50,
            boxShadow: hovering ? '0 6px 16px rgba(0, 0, 0, 0.12)' : '',
          }}
        >
          <div className='relative py-[25px] px-[3px]' style={{ height: '65px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
            {/* Logo could go here */}
          </div>

          {/* Menu Lateral */}
          <div className='flex flex-col justify-between h-[90%]'>
            <Menu
              className='menu-sidebar'
              theme="light"
              defaultSelectedKeys={['1']}
              mode="inline"
              items={items}
            />
          </div>
        </Sider>
        {hovering && collapsed && (
          <div style={{ width: '80px', flexShrink: 0 }} />
        )}
      </div>

      <Layout>
        <Header className='shadow-sm py-0 mx-[30px] px-[10px] bg-[#FFFFFF] h-[62px] rounded-bl-[7px] rounded-br-[7px]'>
          <div className='flex justify-between items-center h-full'>
            <div>
              <Button
                className='button-collapse h-9 !w-9 rounded-full bg-[#e0e6ed66]'
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setCollapsed(!collapsed)}
              />
            </div>
            <div
              className='flex justify-end gap-[10px] p-[10px] items-start'
            >
              <MenuProfile />
            </div>
          </div>
        </Header>
        <Content
          style={{
            margin: '0 16px',
          }}
        >
          <div
            className="m-4"
          >
            <Outlet />
          </div>
        </Content>
        {/* <Footer
          style={{
            textAlign: 'center',
          }}
        >
        </Footer> */}
      </Layout>
    </Layout>
  );
};

export default Sidebar;